// ─── localStorage persistence ───

// NOTE: setSecure/getSecure use a per-origin deterministic key (PBKDF2 over a
// fixed label). This is *obfuscation*, not real encryption — a script running on
// this origin can re-derive the key trivially. Treat it as protection against a
// casual user inspecting localStorage in DevTools, NOT against XSS. Anything
// truly sensitive must live behind an httpOnly server-set cookie.
const _LS_CRYPTO_ALGO = { name: 'AES-GCM', length: 256 };
const _LS_CRYPTO_IV_LENGTH = 12; // bytes
const _LS_CRYPTO_KEY_LABEL = 'ironlog-ls-key-v1';
const _ENC_MARKER = 'enc1:'; // marker prefix on encrypted values

async function _getLsCryptoKey() {
  if (typeof window === 'undefined' || !('crypto' in window) || !window.crypto.subtle) return null;
  const enc = new TextEncoder();
  const material = enc.encode(_LS_CRYPTO_KEY_LABEL + '|' + window.location.origin);
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw', material, { name: 'PBKDF2' }, false, ['deriveKey']
  );
  return window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode('ironlog-ls-salt'), iterations: 100000, hash: 'SHA-256' },
    keyMaterial, _LS_CRYPTO_ALGO, false, ['encrypt', 'decrypt']
  );
}

async function _lsEncrypt(plainText) {
  const key = await _getLsCryptoKey();
  if (!key) return null;
  const enc = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(_LS_CRYPTO_IV_LENGTH));
  const cipherBuf = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plainText));
  const buff = new Uint8Array(cipherBuf);
  const combined = new Uint8Array(iv.byteLength + buff.byteLength);
  combined.set(iv, 0); combined.set(buff, iv.byteLength);
  let binary = '';
  for (let i = 0; i < combined.byteLength; i++) binary += String.fromCharCode(combined[i]);
  return _ENC_MARKER + btoa(binary);
}

async function _lsDecrypt(cipherTextB64) {
  const key = await _getLsCryptoKey();
  if (!key) return null;
  try {
    const binary = atob(cipherTextB64);
    const combined = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) combined[i] = binary.charCodeAt(i);
    const iv = combined.slice(0, _LS_CRYPTO_IV_LENGTH);
    const data = combined.slice(_LS_CRYPTO_IV_LENGTH);
    const plainBuf = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
    return new TextDecoder().decode(plainBuf);
  } catch (e) { return null; }
}

// Surface quota failures to the UI; default handler logs and dispatches an event.
let _quotaListener = null;
export const setQuotaHandler = (fn) => { _quotaListener = fn; };

export const LS={
  get:(k)=>{try{const v=localStorage.getItem(k);return v?JSON.parse(v):null;}catch(e){return null;}},
  del:(k)=>{try{localStorage.removeItem(k);return true;}catch(e){return false;}},
  set:(k,v)=>{
    try{localStorage.setItem(k,JSON.stringify(v));return true;}
    catch(e){
      if(e && (e.name==='QuotaExceededError' || e.code===22 || e.code===1014)){
        try{ _quotaListener && _quotaListener({key:k,error:e}); }catch{}
        try{ window.dispatchEvent(new CustomEvent('ironlog:quota-exceeded',{detail:{key:k}})); }catch{}
      }
      console.warn('LS.set failed for',k,e);
      return false;
    }
  },
  /**
   * Store a value with light obfuscation. If SubtleCrypto is unavailable
   * (non-secure context), REFUSES to write rather than silently degrading to
   * cleartext — callers must handle a false return. See file header.
   */
  setSecure: async (k,v)=>{try{
    const serialized = JSON.stringify(v);
    const encrypted = await _lsEncrypt(serialized);
    if (encrypted === null){
      console.warn('LS.setSecure: encryption unavailable; refusing to store',k,'in cleartext');
      return false;
    }
    localStorage.setItem(k, encrypted);
    return true;
  }catch(e){console.warn('LS.setSecure failed for',k,e);return false;}},
  /**
   * Retrieve an obfuscated value. Returns null on missing/corrupt.
   * Marker prefix distinguishes encrypted blobs from legacy plaintext;
   * legacy values are re-encrypted in place on first read (self-healing).
   */
  getSecure: async (k)=>{try{
    const stored = localStorage.getItem(k);
    if (!stored) return null;
    if (stored.startsWith(_ENC_MARKER)){
      const decrypted = await _lsDecrypt(stored.slice(_ENC_MARKER.length));
      if (decrypted===null) return null;
      try{return JSON.parse(decrypted);}catch{return null;}
    }
    // Legacy path: stored as plaintext JSON. Parse it, then opportunistically
    // migrate the value to encrypted-at-rest so it never stays in cleartext.
    let val;
    try{val=JSON.parse(stored);}catch{return null;}
    if (val!==null && val!==undefined){
      _lsEncrypt(stored).then(enc=>{
        if (enc!==null){try{localStorage.setItem(k,enc);}catch{}}
      }).catch(()=>{});
    }
    return val;
  }catch(e){console.warn('LS.getSecure failed for',k,e);return null;}},
};

// ─── Cookie helper (for SSO across ironlog.space subdomains) ───
// Note: server-set HttpOnly cookies are preferable; this client-side helper is
// JS-readable on purpose so the SPA can read the session token.
const _escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// Exact-or-subdomain match — a bare endsWith("ironlog.space") would also
// match lookalike hosts such as evilironlog.space.
const _isIronlogHost = () => {
  const h = window.location.hostname;
  return h === 'ironlog.space' || h.endsWith('.ironlog.space');
};
export const Cookie={
  get:(name)=>{
    if(typeof document==='undefined')return null;
    const m=document.cookie.match(new RegExp('(?:^|;\\s*)'+_escapeRe(name)+'=([^;]*)'));
    return m?decodeURIComponent(m[1]):null;
  },
  set:(name,value,days=30)=>{
    if(typeof document==='undefined')return;
    const exp=new Date(Date.now()+days*864e5).toUTCString();
    const domainAttr=_isIronlogHost()?";domain=.ironlog.space":"";
    document.cookie=`${encodeURIComponent(name)}=${encodeURIComponent(value)};expires=${exp}${domainAttr};path=/;secure;samesite=strict`;
  },
  clear:(name)=>{
    if(typeof document==='undefined')return;
    const domainAttr=_isIronlogHost()?";domain=.ironlog.space":"";
    document.cookie=`${encodeURIComponent(name)}=;expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr};path=/;secure;samesite=strict`;
  },
};
